import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useEscapeKey } from './hooks/useEscapeKey';
import { useToast } from './components/ui/Toast';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const TechStackCRUD = () => {
  const [techStacks, setTechStacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTechStack, setEditingTechStack] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    icon: '',
    color: 'bg-gray-500',
    sort_order: 0,
    is_active: true
  });

  // Initialize toast and escape key hook
  const { toast } = useToast();
  useEscapeKey(() => setShowModal(false), showModal);

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchTechStacks();
  }, []);

  const fetchTechStacks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/techstack?active_only=false`);
      setTechStacks(response.data);
    } catch (error) {
      console.error('Error fetching tech stacks:', error);
      alert('Error fetching tech stacks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const authHeaders = getAuthHeaders();
      
      if (editingTechStack) {
        // Update existing tech stack
        await axios.put(`${API_BASE_URL}/api/admin/techstack/${editingTechStack.id}`, formData, {
          headers: authHeaders
        });
        toast.success('Tech stack updated successfully!');
      } else {
        // Create new tech stack
        await axios.post(`${API_BASE_URL}/api/admin/techstack`, formData, {
          headers: authHeaders
        });
        toast.success('Tech stack created successfully!');
      }

      setShowModal(false);
      setEditingTechStack(null);
      resetForm();
      fetchTechStacks();
    } catch (error) {
      console.error('Error saving tech stack:', error);
      toast.error(editingTechStack ? 'Error updating tech stack.' : 'Error creating tech stack.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (techStack) => {
    setEditingTechStack(techStack);
    setFormData({
      name: techStack.name || '',
      display_name: techStack.display_name || '',
      description: techStack.description || '',
      icon: techStack.icon || '',
      color: techStack.color || 'bg-gray-500',
      sort_order: techStack.sort_order || 0,
      is_active: techStack.is_active !== undefined ? techStack.is_active : true
    });
    setShowModal(true);
  };

  const handleDelete = async (techStackId) => {
    if (window.confirm('Are you sure you want to delete this tech stack?')) {
      try {
        const authHeaders = getAuthHeaders();
        await axios.delete(`${API_BASE_URL}/api/admin/techstack/${techStackId}`, {
          headers: authHeaders
        });
        toast.success('Tech stack deleted successfully!');
        fetchTechStacks();
      } catch (error) {
        console.error('Error deleting tech stack:', error);
        toast.error('Error deleting tech stack.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      icon: '',
      color: 'bg-gray-500',
      sort_order: 0,
      is_active: true
    });
  };

  const handleAddNew = () => {
    setEditingTechStack(null);
    resetForm();
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tech stacks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tech Stack Management</h1>
          <p className="text-gray-600">Manage technology stacks for accelerator compatibility</p>
        </div>
        <button
          onClick={handleAddNew}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <span className="text-lg">+</span>
          <span>Add Tech Stack</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tech Stack
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sort Order
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {techStacks.map((techStack) => (
              <tr key={techStack.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{techStack.icon}</span>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {techStack.display_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {techStack.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate">
                    {techStack.description}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {techStack.sort_order}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    techStack.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {techStack.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(techStack)}
                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(techStack.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {techStacks.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">No tech stacks found</div>
            <p className="text-gray-400 mt-2">Add your first tech stack to get started</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTechStack ? 'Edit Tech Stack' : 'Add New Tech Stack'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tech Stack Name (lowercase identifier)
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., react, nodejs, python"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.display_name}
                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., React.js, Node.js, Python"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows="3"
                    placeholder="Describe this technology stack..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icon (emoji)
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.icon}
                    onChange={(e) => setFormData({...formData, icon: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="⚛️"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.sort_order}
                    onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="0"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : (editingTechStack ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechStackCRUD;